'use client';

import { forwardRef, useEffect, useState } from 'react';
import Image from 'next/image';

/**
 * Drop-in replacement for next/image for remote photos.
 *
 * Remote images go through the self-hosted optimizer (/_next/image). When it is
 * saturated it times out and the upstream returns 502, which leaves a broken
 * image icon. On error we retry once with the original URL, bypassing the
 * optimizer, so the user always gets the photo.
 */
const SafeImage = forwardRef(function SafeImage(
  { src, unoptimized, onError, ...props },
  ref
) {
  const [bypassOptimizer, setBypassOptimizer] = useState(false);

  useEffect(() => {
    setBypassOptimizer(false);
  }, [src]);

  const isRemote = typeof src === 'string' && /^https?:\/\//.test(src);

  return (
    <Image
      ref={ref}
      src={src}
      unoptimized={unoptimized || bypassOptimizer}
      onError={(e) => {
        if (isRemote && !unoptimized && !bypassOptimizer) {
          setBypassOptimizer(true);
        }
        onError?.(e);
      }}
      {...props}
    />
  );
});

export default SafeImage;
